if (!customElements.get('order-reorder')) {
  customElements.define(
    'order-reorder',
    class OrderReorder extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-reorder-button]');
        this.button?.addEventListener('click', () => this.reorder());
        // Coming back from the cart with the browser's back button shows the page as it was left ("Adding…").
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) this.setLoading(false);
        });
      }

      orderData() {
        const data = JSON.parse(this.querySelector('[data-reorder-data]').textContent);
        data.lines.forEach((line) => (line.properties = this.toObject(line.properties)));
        return data;
      }

      // Liquid may serialize line properties as [[name, value], ...], [{ name, value }, ...] or { name: value }.
      toObject(properties) {
        if (!properties) return {};
        if (!Array.isArray(properties)) return { ...properties };
        return Object.fromEntries(
          properties.map((entry) => (Array.isArray(entry) ? entry : [entry.name ?? entry.first, entry.value ?? entry.last]))
        );
      }

      // Rebuilds the order as it was configured: each article ("_Bulk group") gets a new code so its sizes are
      // grouped again in the cart, without mixing with an identical article already there.
      buildItems(lines) {
        const groups = new Map();
        lines.forEach((line, index) => {
          const key = line.properties['_Bulk group'] || `line-${index}`;
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(line);
        });

        const items = [];
        const notes = [];

        groups.forEach((groupLines) => {
          const garments = [];
          groupLines
            .filter((line) => !line.addon)
            .forEach((line) => {
              if (!line.available || line.max === 0) {
                notes.push(`${line.label} is no longer available.`);
                return;
              }
              let quantity = line.quantity;
              if (line.max !== null && quantity > line.max) {
                quantity = line.max;
                notes.push(`${line.label}: only ${quantity} left, quantity adjusted.`);
              }
              garments.push({ line, quantity });
            });

          // No garment left in this article: its print placements are not added either.
          if (!garments.length) return;

          const hasGroup = Boolean(groupLines[0].properties['_Bulk group']);
          const groupId = hasGroup ? this.newGroupId() : null;
          const withGroup = (properties) => (groupId ? { ...properties, '_Bulk group': groupId } : { ...properties });
          const pieces = garments.reduce((sum, garment) => sum + garment.quantity, 0);

          garments.forEach(({ line, quantity }) => {
            items.push({ id: line.variantId, quantity, properties: withGroup(line.properties) });
          });

          // Placements follow the number of pieces, like when quantities are edited in the cart.
          groupLines
            .filter((line) => line.addon && line.available)
            .forEach((line) => {
              items.push({ id: line.variantId, quantity: pieces, properties: withGroup(line.properties) });
            });
        });

        return { items, notes };
      }

      async reorder() {
        if (this.button.disabled) return;
        this.showMessage(null);

        const { items, notes } = this.buildItems(this.orderData().lines);
        if (!items.length) {
          this.showMessage('The items from this order are no longer available.');
          return;
        }

        this.setLoading(true);
        try {
          const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const json = await response.json();
          if (!response.ok || json.status) throw new Error(json.description || json.message || 'Something went wrong.');

          if (!notes.length) {
            window.location = this.dataset.cartUrl || '/cart';
            return;
          }
          this.showMessage(notes.join(' '), true);
          this.setLoading(false);
        } catch (error) {
          this.showMessage(error.message || 'Something went wrong. Please try again.');
          this.setLoading(false);
        }
      }

      newGroupId() {
        // Same short readable code as the configurator, e.g. "#A7K3" (no 0/O/1/I).
        const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        const values = new Uint8Array(4);
        if (window.crypto?.getRandomValues) window.crypto.getRandomValues(values);
        else values.forEach((_, i) => (values[i] = Math.floor(Math.random() * 256)));
        return '#' + Array.from(values, (v) => alphabet[v % alphabet.length]).join('');
      }

      setLoading(loading) {
        this.button.disabled = loading;
        this.classList.toggle('is-loading', loading);
        this.querySelector('[data-reorder-label]').textContent = loading ? 'Adding…' : 'Reorder';
      }

      // withCartLink: the order was added but something changed (stock...): explain it, then let them go to the cart.
      showMessage(message, withCartLink = false) {
        const el = this.querySelector('[data-reorder-message]');
        if (!el) return;
        el.textContent = message || '';
        if (message && withCartLink) {
          const link = document.createElement('a');
          link.href = this.dataset.cartUrl || '/cart';
          link.className = 'reorder__message-link';
          link.textContent = 'Go to cart';
          el.append(' ', link);
        }
        el.classList.toggle('is-success', withCartLink);
        el.hidden = !message;
      }
    }
  );
}
